import { useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';

import { authorizedAxiosInstance } from '@/clients';

import { CALENDAR_EVENT_SOURCES, type CalendarEventSourceId, type MarketCalendarEvent } from './calendarEvents';

interface CalendarEventDateRange {
    from: string;
    to: string;
}

interface CalendarEventsState {
    events: MarketCalendarEvent[];
    failedSources: CalendarEventSourceId[];
    loading: boolean;
}

const sortEvents = (left: MarketCalendarEvent, right: MarketCalendarEvent): number =>
    left.startsAt.localeCompare(right.startsAt) || left.title.localeCompare(right.title);

export const useCalendarEvents = ({ from, to }: CalendarEventDateRange): CalendarEventsState => {
    const [events, setEvents] = useState<MarketCalendarEvent[]>([]);
    const [failedSources, setFailedSources] = useState<CalendarEventSourceId[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const abortController = new AbortController();

        const loadEvents = async () => {
            setLoading(true);

            const results = await Promise.allSettled(
                CALENDAR_EVENT_SOURCES.map(async (source) => {
                    const response = await authorizedAxiosInstance.get<unknown>(source.endpoint, {
                        params: { from, to },
                        signal: abortController.signal,
                        timeout: 30_000,
                    });

                    return {
                        events: source.normalize(response.data),
                        source: source.id,
                    };
                }),
            );

            if (abortController.signal.aborted) {
                return;
            }

            const nextEvents: MarketCalendarEvent[] = [];
            const nextFailedSources: CalendarEventSourceId[] = [];

            results.forEach((result, index) => {
                const source = CALENDAR_EVENT_SOURCES[index];
                if (!source) {
                    return;
                }

                if (result.status === 'fulfilled') {
                    nextEvents.push(...result.value.events);
                    return;
                }

                nextFailedSources.push(source.id);
                captureException(result.reason, {
                    tags: {
                        calendarEventSource: source.id,
                    },
                });
            });

            setEvents(nextEvents.sort(sortEvents));
            setFailedSources(nextFailedSources);
            setLoading(false);
        };

        void loadEvents();

        return () => abortController.abort();
    }, [from, to]);

    return useMemo(
        () => ({
            events,
            failedSources,
            loading,
        }),
        [events, failedSources, loading],
    );
};
